<script setup lang="ts">
import { nextTick, onBeforeUnmount, onMounted, ref } from 'vue'
import SubjectCreditTable from '@/components/credit-search/SubjectCreditTable.vue'
import PathwayCreditTable from '@/components/credit-search/PathwayCreditTable.vue'
import type { CreditSearchPreviewData } from '@/models/credit-search/preview'
import { getUrl } from '@/common/utility'

defineOptions({ name: 'CreditSearchCreditPreviewModal' })

const props = defineProps<{
  preview: CreditSearchPreviewData | null
  loading: boolean
  refreshing: boolean
  errorMessage: string | null
  applyNowVisible: boolean
  selectedCourseKey: string | null
}>()

const emit = defineEmits<{
  close: []
  disclaimer: []
  removeSubject: [selectionKey: string]
  removeCourse: [selectionKey: string]
}>()

const dialog = ref<HTMLDivElement | null>(null)
let closeRequested = false

function requestClose(): void {
  if (closeRequested || props.refreshing) return
  closeRequested = true
  emit('close')
}

function onKeydown(event: KeyboardEvent): void {
  if (event.key === 'Escape') {
    event.preventDefault()
    requestClose()
    return
  }

  if (event.key !== 'Tab' || !dialog.value) return
  const focusable = Array.from(dialog.value.querySelectorAll<HTMLElement>(
    'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
  ))
  if (focusable.length === 0) return
  const first = focusable[0]
  const last = focusable[focusable.length - 1]
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault()
    last?.focus()
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault()
    first?.focus()
  }
}

onMounted(() => {
  document.body.classList.add('modal-open')
  window.addEventListener('keydown', onKeydown)
  void nextTick(() => dialog.value?.focus())
})

onBeforeUnmount(() => {
  document.body.classList.remove('modal-open')
  window.removeEventListener('keydown', onKeydown)
})
</script>

<template>
  <div
    id="ShowCreditModal"
    class="modal fade show credit-preview-modal"
    role="dialog"
    aria-modal="true"
    aria-labelledby="credit-preview-title"
    @mousedown.self="requestClose"
  >
    <div ref="dialog" class="modal-dialog modal-lg" role="document" tabindex="-1">
      <div class="modal-content">
        <div class="modal-header">
          <h2 id="credit-preview-title" class="modal-title">Potential Credit</h2>
          <button type="button" class="btn-close" aria-label="Close" @click="requestClose"></button>
        </div>
        <div id="creditOutputTableBody" class="modal-body">
          <p v-if="props.loading">Loading potential credit...</p>
          <p v-else-if="props.errorMessage" class="alert alert-danger" role="alert">{{ props.errorMessage }}</p>
          <template v-else-if="props.preview">
            <template v-for="(course, index) in props.preview.submission.external_courses" :key="`${course.external_course_name}-${index}`">
              <section v-if="course.subjects.length > 0" class="credit-preview-section">
                <div class="instNameHeader">{{ course.external_course_institution.name }}</div>
                <h3>{{ course.external_course_name }}</h3>
                <SubjectCreditTable
                  :course="course"
                  :refreshing="props.refreshing"
                  @remove="emit('removeSubject', $event)"
                />
              </section>
              <section v-if="course.articulation_agreements.length > 0" class="credit-preview-section">
                <div class="instNameHeader">{{ course.external_course_institution.name }}</div>
                <h3>{{ course.external_course_name }}</h3>
                <PathwayCreditTable
                  :course="course"
                  :refreshing="props.refreshing"
                  :selection-key="props.selectedCourseKey ?? ''"
                  @remove="emit('removeCourse', $event)"
                />
              </section>
            </template>
          </template>
          <em>
            *** Please note the
            <a id="disclaimerLink" href="#top" @click.prevent="emit('disclaimer')">disclaimer information</a>
            as provided on the search page ***
          </em>
          <fieldset>
            <legend>Save Credit</legend>
            <p>You can save your credit and submit it as part of your course application to La Trobe.</p>
            <div class="alert alert-danger" role="alert" hidden></div>
          </fieldset>
        </div>
        <div class="modal-footer">
          <a
            v-if="props.applyNowVisible && props.preview"
            id="openFormBtn"
            class="btn btn-primary"
            target="_blank"
            :href="getUrl(`form/?reference=${encodeURIComponent(props.preview.submission.reference)}`)"
          >Apply Now (Current Students Only)</a>
          <a
            v-if="props.preview"
            id="saveBtn"
            class="btn btn-secondary"
            target="_blank"
            :href="getUrl(`api/calc/submission/preview/pdf?reference=${encodeURIComponent(props.preview.submission.reference)}`)"
          >Download PDF</a>
          <button type="button" class="btn btn-success" @click="requestClose">Close</button>
        </div>
      </div>
    </div>
  </div>
  <div class="modal-backdrop fade show" @click="requestClose"></div>
</template>

<style lang="scss" scoped>
.credit-preview-modal {
  display: block;
  overflow-x: hidden;
  overflow-y: auto;

  .modal-title {
    margin: 0;
  }

  .btn-close {
    margin-left: auto;
  }

  .credit-preview-section + .credit-preview-section {
    margin-top: 1.5rem;
  }

  .instNameHeader {
    color: #e42312;
    font-family: 'Roboto', sans-serif;
    font-weight: 700;
    font-size: 1.5rem;
    line-height: 1.2;
  }
}
</style>
